import { useEffect, useReducer } from 'react';
import { autoBeforeEachTest } from '@jira/platform__utils-auto-before-each-test/src/auto-before-each-test.tsx';
import type { AdaptiveLanguageState } from './adaptiveLanguageState.tsx';

type Listener = () => void;

const DEFAULT_STATE: AdaptiveLanguageState = {
	domain: null,
	glossary: null,
	status: 'idle',
};

let state: AdaptiveLanguageState = DEFAULT_STATE;
const listeners = new Set<Listener>();
autoBeforeEachTest(() => {
	state = DEFAULT_STATE;
	listeners.clear();
});

function emitChange() {
	listeners.forEach((listener) => listener());
}

export const adaptiveLanguageStore = {
	getState(): AdaptiveLanguageState {
		return state;
	},

	setState(next: AdaptiveLanguageState): void {
		state = next;
		emitChange();
	},

	reset(): void {
		state = DEFAULT_STATE;
		emitChange();
	},

	subscribe(listener: Listener): () => void {
		listeners.add(listener);
		return () => listeners.delete(listener);
	},
};

/**
 * React hook to subscribe to the adaptive language store.
 * Uses useReducer as a compatible alternative to useSyncExternalStore.
 * Re-renders only when state changes.
 */
export function useAdaptiveLanguageStore(): AdaptiveLanguageState {
	const [, forceUpdate] = useReducer((x: number) => x + 1, 0);

	useEffect(() => {
		// Re-subscribe on mount, unsubscribe on unmount
		return adaptiveLanguageStore.subscribe(forceUpdate);
	}, []);

	return adaptiveLanguageStore.getState();
}
